<template>
  <div v-show="isActive" class="ak-notify" :class="classes">
    <ak-icon v-if="hasIcon && computedIcon" :type="computedIcon" :size="iconSize" class="ak-notify__icon" />
    <div v-if="message" v-html="message"></div>
    <slot else />
    <ak-close
      v-if="closable"
      :inverse="type === 'grey' || epic"
      class="ak-notify__close"
      :class="{ 'ak-notify__close--inverse': type === 'grey' || epic }"
      :aria-label="ariaCloseLabel"
      @click="close"
    />
  </div>
</template>

<script>
import { defineComponent, computed } from 'vue'
import Icon from '../icon/Icon.vue'
import Close from '../close/Close.vue'
import Pack from '../pack/Pack.vue'
import PackItem from '../pack/PackItem.vue'

import {
  notificationProps,
  messageProps,
  messageMixin,
} from '../../mixins/messageMixin'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-notification :epic=false type="info"></ak-notification>
   */
  name: 'AkNotification',

  /**
   * @type {Object}
   * @description A collection of components that this component
   * is dependant on.
   */
  components: {
    [Icon.name]: Icon,
    [Close.name]: Close,
    [Pack.name]: Pack,
    [PackItem.name]: PackItem,
  },

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    ...notificationProps,
    ...messageProps,
  },

  /**
   * @type {Array}
   * @description The list of events that this component can trigger
   */
  emits: ['close', 'update:active'],

  /**
   * @param {Object} props - The properties of the component
   * @param {Object} options - The setup options
   * @param {Function} options.emit - The emit method for triggering events
   * @description Sets up the component and returns an object containing all of the
   * attributes for the component to use.
   */
  setup(props, { emit }) {
    const mixin = messageMixin({
      props,
      emit,
    })

    /**
     * @returns {String}
     * @description Returns the classes that need to be applied to
     * the notification
     */
    const classes = computed(() => {
      return {
        [`ak-notify--${props.type}`]: props.type,
        'ak-notify--epic': props.epic,
        'ak-notify--dismissible': props.closable,
        'ak-notify--large': props.large,
        'ak-notify--large-center': props.center,
      }
    })

    return {
      ...mixin,
      classes,
    }
  },
})
</script>

<style lang="scss">
@import '../../scss/_settings.scss';
@import '../../scss/_mixins/_custom';
.ak-notify {
  $self: &;
  display: inline-flex;
  padding: halve($spacing);
  border-radius: $base-border-radius;
  background-color: #fff;
  color: $color-grey-dark;
  border: 1px solid #fff;

  &:first-letter{
    text-transform: capitalize
  }

  &__icon {
    font-size: 100%;
    margin-right: $spacing-tiny;
    margin-bottom: halve($spacing-tiny);
    line-height: 1.4;
  }

  &__close {
    color: rgba(0, 0, 0, 0.5);
    position: absolute;
    top: 0.625rem;
    right: 0.625rem;

    &--inverse {
      color: rgba(255, 255, 255, 0.7);
    }
  }

  &--dismissible {
    position: relative;
    padding-right: calc(#{$spacing} + 1.25rem);
  }

  &--primary {
    background-color: $color-primary-light;
    color: darken($color-primary, 25%);
    border-color: $color-primary;

    #{$self}__icon {
      color: $color-primary;
    }

    &#{$self}--epic {
      color: #fff;
      background-color: $color-primary;
    }
  }

  &--info {
    background-color: lighten($color-info, 35%);
    color: darken($color-info, 25%);
    border-color: $color-info;

    #{$self}__icon {
      color: $color-info;
    }

    &#{$self}--epic {
      color: #fff;
      background-color: $color-info;
    }
  }

  &--warning {
    background-color: $color-warning-light;
    color: darken($color-warning, 25%);
    border-color: $color-warning;

    #{$self}__icon {
      color: $color-warning;
    }

    &#{$self}--epic {
      color: #fff;
      background-color: $color-warning;
    }
  }

  &--danger {
    background-color: $color-danger-light;
    color: darken($color-danger, 25%);
    border-color: $color-danger;

    #{$self}__icon {
      color: $color-danger;
    }

    &#{$self}--epic {
      color: #fff;
      background-color: $color-danger;
    }
  }

  &--success {
    background-color: $color-success-light;
    color: darken($color-success, 25%);
    border-color: $color-success;

    #{$self}__icon {
      color: $color-success;
    }

    &#{$self}--epic {
      color: #fff;
      background-color: $color-success;
    }
  }

  &--grey {
    background-color: $color-grey-dark;
    color: #fff;
    border-color: $color-grey-dark;

    #{$self}__icon {
      color: #fff;
    }
  }

  &--epic {
    a,
    #{$self}__icon {
      color: currentColor;
    }
  }

  &--large {
    padding: $spacing;
    display: flex;

    &-center {
      align-items: center;
    }

    .ak-notify__icon {
      margin-right: $spacing-small;
      border-radius: 50%;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 38px;
      min-width: 38px;
      height: 38px;
      background-color: white;
      &.icon {
        font-size: 130%;
      }
    }

    &-compact {
      padding: halve($spacing);
      .notify__icon {
        width: 35px;
        min-width: 35px;
        height: 35px;
        &.icon {
          font-size: 110%;
        }
      }
    }

    &--small {
      font-size: smaller;
      padding: quarter($spacing);

      .notify__icon {
        margin-right: halve($spacing-tiny);
        margin-bottom: quarter($spacing-tiny);
      }

      &-center {
        display: flex;
        align-items: center;
      }
    }

    &--with-action {
      display: flex;
      align-items: center;
      .notify__icon {
        margin-bottom: 0;
      }
      .notify__action {
        margin-left: auto;
        flex-shrink: 0;
      }
    }
  }
}
</style>
